import { Box, Button, Dialog, DialogActions, DialogTitle, DialogContent, IconButton, DialogProps } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { Folder } from '../../types';
import { styles } from './DuplicateItemsDialog.styles';
import { useParentFolder } from '../LocationSelect/hooks/useParentFolder';
import { LocationSelect } from '../LocationSelect/LocationSelect';

const intl = {
  title: 'Duplicate Items',
  cancel: 'Cancel',
  duplicate: 'Duplicate',
  closeIconAriaLabel: 'close button for dialog'
};

type DuplicateItemsDialogProps = DialogProps & {
  title?: string;
  isDuplicating?: boolean;
  currentFolderId?: Folder['id'];
  onDuplicate: (parentFolderId: Folder['id']) => void;
  onClose: () => void;
};

export function DuplicateItemsDialog({
  title = intl.title,
  isDuplicating = false,
  currentFolderId,
  onDuplicate,
  ...dialogProps
}: DuplicateItemsDialogProps) {
  const { parentFolder, subfolders, setParentFolderId } = useParentFolder(currentFolderId);

  const handleDuplicate = () => {
    onDuplicate(parentFolder.id);
  };

  return (
    <Dialog fullWidth {...dialogProps}>
      <Box sx={styles.header}>
        <DialogTitle>{title}</DialogTitle>
        <IconButton sx={styles.closeIcon} aria-label={intl.closeIconAriaLabel} onClick={dialogProps.onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={styles.content}>
        {dialogProps.children}
        <LocationSelect
          withCreateNewFolder={true}
          checkDepthLimit={true}
          selectedFolder={parentFolder}
          subfolders={subfolders}
          onFolderSelect={setParentFolderId}
        />
      </DialogContent>

      <DialogActions>
        <Button
          disabled={isDuplicating}
          color="secondary"
          data-test="cancelDuplicateButton"
          onClick={dialogProps.onClose}
        >
          {intl.cancel}
        </Button>
        <LoadingButton
          loading={isDuplicating}
          color="primary"
          variant="contained"
          data-test="duplicateButton"
          onClick={handleDuplicate}
        >
          {intl.duplicate}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
